import React, { useState, useEffect, useMemo } from "react";
import "./App.css";


const positionOptions = ["All", "QB", "RB", "WR", "TE"];

export default function App() {
  const [filter, setFilter] = useState("All");
  const [players, setPlayers] = useState([]);
  const [message, setMessage] = useState("");
  const [yearFilter, setYearFilter] = useState(2026);
  const [selectedRound, setSelectedRound] = useState(null);


  const API_BASE = process.env.REACT_APP_API_BASE || "/fa-api";


const yearOptions = Array.from(
  new Set(players.map((p) => p.nfl_draft_year).filter(Boolean))
).sort((a, b) => {
  if (a === 2026) return -1; // force 2026 first
  if (b === 2026) return 1;
  return a - b; // ascending for others
  });

  // Fetch players
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/get-prospect-rankings`, {
          method: "GET",
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setPlayers(Array.isArray(data) ? data : data.players ?? []);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("fetch rankings error:", err);
        setMessage("Failed to load rankings");
      }
    })();
    return () => controller.abort();
  }, [API_BASE]);

  // Add position rank
  const playersWithPositionRank = useMemo(() => {
    const counters = {};
    return players.map((p) => {
      const pos = p?.metrics?.position || "UNK";
      counters[pos] = (counters[pos] || 0) + 1;
      return { ...p, positionRank: `${pos}${counters[pos]}` };
    });
  }, [players]);

  // Base filter
  const baseFiltered = useMemo(() => {
    return playersWithPositionRank.filter((p) => {
      const matchPosition = filter === "All" || p?.metrics?.position === filter;
      const matchYear = !yearFilter || p.nfl_draft_year === yearFilter;
      return matchPosition && matchYear;
    });
  }, [playersWithPositionRank, filter, yearFilter]);

  // Group by Round → Early/Mid/Late
  const playersByRound = useMemo(() => {
    const grouped = {};
    baseFiltered.forEach((p) => {
      if (!p.draft_pick_capital) return;
      const [roundStr, pickStr] = p.draft_pick_capital.split(".");
      const round = parseInt(roundStr, 10);
      const pick = parseInt(pickStr, 10);

      let sub;
      if (pick <= 4) sub = "Early";
      else if (pick <= 8) sub = "Mid";
      else sub = "Late";

      if (!grouped[round]) {
        grouped[round] = { Early: [], Mid: [], Late: [] };
      }
      grouped[round][sub].push(p);
    });
    return grouped;
  }, [baseFiltered]);

  return (
    <div
      style={{
        padding: "24px",
        fontFamily: "Space Grotesk",
        backgroundColor: "#6a11cb",
        minHeight: "100vh",
        color: "#000",
        boxShadow: "inset 0 0 40px rgba(0,0,0,0.2)",
      }}
    >
      {/* Header */}
      <header
        style={{
          textAlign: "center",
          padding: "12px",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          borderRadius: "8px",
          marginBottom: "20px",
          fontSize: "1.2rem",
          fontWeight: "600",
          color: "#000",
        }}
      >
Want to view rankings, scores and profiles on 120+ prospects? Join Early Access for a 67% discount & to get notified when prospect scores and profiles go live. Early access is limted to the first 100 signups! · fantasyamp.com/subscribe
</header>
<h1
  style={{
    textAlign: "center",
    marginBottom: "24px",
    fontSize: "2.4rem",
    fontWeight: 800,
    letterSpacing: "1px",
    textShadow: "2px 2px 6px rgba(0,0,0,0.3)",
    color: "#FFFFFF",
  }}
>
  <span style={{ display: "block" }}>AMPLive ⚡</span>
  <span style={{ display: "block", fontSize: "1.6rem", fontWeight: 600 }}>
    Dynamic Prospect Rankings
  </span>
</h1>

      {/* Position Filter */}
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {positionOptions.map((pos) => (
            <button
              key={pos}
              onClick={() => setFilter(pos)}
              style={{
                padding: "8px 16px",
                fontSize: "1.1rem",
                fontWeight: "bold",
                borderRadius: 6,
                border: "1px solid #ccc",
                backgroundColor: filter === pos ? "#FFD700" : "#f5f5f5",
                cursor: "pointer",
                transition: "all .2s ease-in-out",
              }}
            >
              {pos}
            </button>
          ))}
        </div>
      </div>

      {/* Year Filter */}
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {yearOptions.map((yr) => (
            <button
              key={yr}
              onClick={() => setYearFilter(yr)}
              style={{
                padding: "8px 16px",
                fontSize: "1.1rem",
                fontWeight: "bold",
                borderRadius: 6,
                border: "1px solid #ccc",
                backgroundColor: yearFilter === yr ? "#FFD700" : "#f5f5f5",
                cursor: "pointer",
                transition: "all .2s ease-in-out",
              }}
            >
              {`Draft Class ${yr}`}
            </button>
          ))}
        </div>
      </div>

      {/* Round Filter */}
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <button
            onClick={() => setSelectedRound(null)}
            style={{
              padding: "8px 16px",
              fontSize: "1rem",
              fontWeight: "bold",
              borderRadius: 6,
              border: "1px solid #ccc",
              backgroundColor: selectedRound === null ? "#FFD700" : "#f5f5f5",
              cursor: "pointer",
              transition: "all .2s ease-in-out",
            }}
          >
            All Rounds
          </button>
    {Object.keys(playersByRound)
      .filter((round) => Number(round) <= 1)   // limit to 6 rounds
      .map((round) => (
            <button
              key={round}
              onClick={() => setSelectedRound(round)}
              style={{
                padding: "8px 16px",
                fontSize: "1rem",
                fontWeight: "bold",
                borderRadius: 6,
                border: "1px solid #ccc",
                backgroundColor:
                  selectedRound === round ? "#FFD700" : "#f5f5f5",
                color: "#000",
                cursor: "pointer",
                transition: "all .2s ease-in-out",
              }}
            >
              {`Round ${round}`}
            </button>
          ))}
        </div>
      </div>

      {/* ROUND GRID */}
<div style={{ marginTop: 30, padding: 16, borderRadius: 10 }}>
  {Object.entries(playersByRound)
    .filter(([round]) => Number(round) <= 1)  // ⬅️ enforce 6-round limit
    .map(([round, subgroups]) => {
      if (selectedRound && selectedRound !== round) return null;

          const totalPlayers = Object.values(subgroups).flat().length;
          if (totalPlayers === 0) return null;

          return (
  <div key={round} style={{ marginBottom: 30 }}>

    {/* AMP Legend */}
    <div
      style={{
        display: "flex",
        justifyContent: "center", 
        gap: 24,
        fontSize: "0.95rem",
        marginBottom: 12,
        color: "#FFFFFF",
        opacity: 0.9,
        flexWrap: "wrap",
      }}
    >
      <span>
        <strong>A</strong> – Analytics:{" "}
        <span style={{ fontWeight: 400 }}>
          	
🏹 Position based grade using advanced metrics
        </span>
      </span>

      <span>
        <strong>M</strong> – Momentum:{" "}
        <span style={{ fontWeight: 400 }}>
	
🔥 Assessing Steam gained toward draft capital and projected usage
        </span>
      </span>

      <span>
        <strong>P</strong> – Production:{" "}
        <span style={{ fontWeight: 400 }}>
          	
📊 College production evaluated from core statistics
        </span>
      </span>

      <span>  
        <span style={{ fontWeight: 400 }}>
      
Click on a player's name to see his full profile. Use these dynamically updating rankings for an edge in your dynasty league. Stay Amped!
        </span>
      </span> 
    </div>

{/* Round Heading */}
<div
  style={{
    fontWeight: "bold",
    fontSize: "1.75rem",
    marginBottom: 35,
    textAlign: "left",
    backgroundColor: "#eaeaea",
    color: "#000",
    padding: "8px 16px",
    borderRadius: 6,
    width: "fit-content",
    marginLeft: 0,          // reset bad margin
    alignSelf: "flex-start",
  }}
>
  Round {round}
</div>
              {/* Early / Mid / Late groups */}
              {Object.entries(subgroups).map(([label, group]) => {
                if (group.length === 0) return null;
                return (
                  <div key={label} style={{ marginBottom: 20 }}>
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: "1.7rem",
                        marginBottom: 12,
                        textAlign: "center",
                        color:
                          label === "Early"
                            ? "#FFD700"
                            : label === "Mid"
                            ? "#C0C0C0"
                            : label === "Late"
                            ? "#CD7F32"
                            : "#000",
                      }}
                    >
                      {label}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 12,
                        justifyContent: "center",
                      }}
                    >
                      {group.map((player) => (
                        <div
                          key={player.id}
                          style={{
                            backgroundColor: player.drafted ? "#ffe5e5" : "#fff",
                            border: "1px solid #ccc",
                            borderRadius: 8,
                            padding: 10,
                            width: 160,
                            textAlign: "center",
                            boxShadow: "0 4px 12px rgba(0,0,0,.25)",
                            transition: "background-color .3s ease",
                          }}
                        >
                          {/* Side-by-side images */}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 8,
                              marginBottom: 8,
                            }}
                          >
                            {player.image && (
                              <img
                                src={player.image}
                                alt={player.name}
                                style={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                                onError={(e) =>
                                  (e.currentTarget.style.display = "none")
                                }
                              />
                            )}

                            {player.team_image && (
                              <img
                                src={player.team_image}
                                alt={`${player.team} logo`}
                                style={{
                                  width: 40,
                                  height: 40,
                                  objectFit: "contain",
                                }}
                                onError={(e) =>
                                  (e.currentTarget.style.display = "none")
                                }
                              />
                            )}
                          </div>

<a
href={`https://fantasyamp.com/player/${player.first_name.toLowerCase()}_${player.last_name.toLowerCase()}`}
  style={{
    textDecoration: "none",
    color: "inherit",
    cursor: "pointer",
  }}
>
  <div
    style={{
      fontSize: "1rem",
      fontWeight: "bold",
    }}
  >
    {player.first_name}
  </div>

  <div
    style={{
      fontSize: "1rem",
      fontWeight: "bold",
    }}
  >
    {player.last_name}
  </div>
</a>

                          {/* Metrics block */}
                          <div
                            style={{
                              fontSize: ".8rem",
                              color: "#333",
                              lineHeight: "1.4",
                            }}
                          >
                            <div
                              style={{ fontWeight: "600", color: "#222" }}
                            >
                              {player?.metrics?.position ?? "—"}
                              {player?.draft_pick_capital && (
                                <span style={{ marginLeft: 6, color: "#666" }}>
                                  | {player.draft_pick_capital}
                                </span>
                              )}
                            </div>

                            {(player?.height_str ||
                              player?.metrics?.weight) && (
                              <div style={{ marginTop: 2 }}>
                                {player?.height_str && (
                                  <span>{player.height_str}</span>
                                )}
                                {player?.metrics?.weight && (
                                  <span>
                                    {player.height_str ? " • " : ""}
                                    {player.metrics.weight} lbs
                                  </span>
                                )}
                              </div>
                            )}

                            {player?.metrics?.college_team && (
                              <div
                                style={{
                                  marginTop: 2,
                                  color: "#4169e1",
                                  fontWeight: 500,
                                }}
                              >
                                {player.metrics.college_team}
                                <span
                                  title={player.spin ?? "No spin available"}
                                >
                                  {" "}
                                  🔊
                                </span>
                              </div>
                            )}

<div
  style={{
    marginTop: 8,
    display: "flex",
    gap: "8px",
    justifyContent: "center",
  }}
>
  {/* Analytics */}
  <div
    style={{
      background:
        player?.amp_analytics_score >= 80
          ? "#d1fae5"
          : player?.amp_analytics_score >= 60
          ? "#fef9c3"
          : "#fee2e2",
      color:
        player?.amp_analytics_score >= 80
          ? "#065f46"
          : player?.amp_analytics_score >= 60
          ? "#92400e"
          : "#991b1b",
      padding: "4px 8px",
      borderRadius: "12px",
      fontSize: "0.8rem",
      fontWeight: 600,
    }}
  >
    A {player?.amp_analytics_score}
  </div>

  {/* Momentum */}
  <div
    style={{
      background:
        player?.amp_momentum_score >= 80
          ? "#d1fae5"
          : player?.amp_momentum_score >= 60
          ? "#fef9c3"
          : "#fee2e2",
      color:
        player?.amp_momentum_score >= 80
          ? "#065f46"
          : player?.amp_momentum_score >= 60
          ? "#92400e"
          : "#991b1b",
      padding: "4px 8px",
      borderRadius: "12px",
      fontSize: "0.8rem",
      fontWeight: 600,
    }}
  >
    M {player?.amp_momentum_score}
  </div>

  {/* Production */}
  <div
    style={{
      background:
        player?.amp_production_score >= 80
          ? "#d1fae5"
          : player?.amp_production_score >= 60
          ? "#fef9c3"
          : "#fee2e2",
      color:
        player?.amp_production_score >= 80
          ? "#065f46"
          : player?.amp_production_score >= 60
          ? "#92400e"
          : "#991b1b",
      padding: "4px 8px",
      borderRadius: "12px",
      fontSize: "0.8rem",
      fontWeight: 600,
    }}
  >
    P {player?.amp_production_score}
  </div>
</div>

{/* AMP Overall */}
<div
  style={{
    marginTop: 10,
    display: "flex",
    justifyContent: "center",
  }}
>
  <div
    style={{
      background:
        player?.amp_aggregate_score >= 80
          ? "#d1fae5"
          : player?.amp_aggregate_score >= 60
          ? "#fef9c3"
          : "#fee2e2",
      color:
        player?.amp_aggregate_score >= 80
          ? "#065f46"
          : player?.amp_aggregate_score >= 60
          ? "#92400e"
          : "#991b1b",
      padding: "6px 12px",
      borderRadius: "12px",
      fontSize: "0.85rem",
      fontWeight: 700,
    }}
  >
    Total Prospect: {player?.amp_aggregate_score}
  </div>
</div>

                            {player?.consensus_tier && (
                              <div
                                style={{
                                  marginTop: 2,
                                  fontSize: ".75rem",
                                  color: "#000",
                                }}
                              >
                                Consensus: {player.consensus_tier}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

<footer
  style={{
    marginTop: "40px",
    padding: "16px",
    textAlign: "center",
    fontSize: "0.85rem",
    color: "#fff",
    backgroundColor: "rgba(0,0,0,0.4)",
    borderTop: "1px solid rgba(255,255,255,0.2)",
  }}
>
  {" "}
  <a
    href="https://fantasyamp.com/subscribe"
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: "#FFD700", fontWeight: "bold" }}
  >
    fantasyamp.com/subscribe
  </a>
</footer>
    </div>
  );
}
